export function hsv2Rgb(hue: string, saturation: string, value: string): number[] {
	const h = Number(hue) % 360;
	const s = Number(saturation) / 100;
	const v = Number(value) / 100;

	const C = v * s;
	const hPrime = h / 60;
	const X = C * (1 - Math.abs((hPrime % 2) - 1));
	const m = v - C;

	// biome-ignore lint/style/useSingleVarDeclarator: <explanation>
	let r: number, g: number, b: number;

	switch (Math.floor(hPrime)) {
		case 0:
			r = C;
			g = X;
			b = 0;
			break;
		case 1:
			r = X;
			g = C;
			b = 0;
			break;
		case 2:
			r = 0;
			g = C;
			b = X;
			break;
		case 3:
			r = 0;
			g = X;
			b = C;
			break;
		case 4:
			r = X;
			g = 0;
			b = C;
			break;
		case 5:
			r = C;
			g = 0;
			b = X;
			break;
		default:
			r = 0;
			g = 0;
			b = 0;
			break;
	}

	return [Math.round((r + m) * 255), Math.round((g + m) * 255), Math.round((b + m) * 255)];
}

export function rgb2Hsv(r: number, g: number, b: number): number[] {
	// biome-ignore lint/style/noParameterAssign:
	r /= 255; // Normalize r to [0-1]
	// biome-ignore lint/style/noParameterAssign:
	g /= 255; // Normalize g to [0-1]
	// biome-ignore lint/style/noParameterAssign:
	b /= 255; // Normalize b to [0-1]

	const max = Math.max(r, g, b);
	const min = Math.min(r, g, b);
	const delta = max - min;

	let h = 0;

	// Calc hue
	if (max === r) {
		h = ((g - b) / delta) % 6;
	} else if (max === g) {
		h = (b - r) / delta + 2;
	} else if (max === b) {
		h = (r - g) / delta + 4;
	}

	// Convert hue to deg
	h = Math.round(h * 60);
	if (h < 0) {
		h += 360;
	}

	return [
		h, // [0-360]
		Math.round((max === 0 ? 0 : delta / max) * 100), // Calc saturation [0-100]
		Math.round(max * 100), // Normalize value to [0-100]
	];
}

export function hex2rgb(color: string) {
	const hex = color.replace('#', '');

	if (hex.length === 6 && new RegExp(/^[0-9a-fA-F]*$/).test(hex)) {
		const rgb: number[] = [];
		for (let i = 0; i <= 4; i += 2) {
			rgb.push(Number.parseInt(hex.substring(i, i + 2), 16));
		}
		return rgb;
	}
	return [0, 0, 0];
}

export function rgb2hex(r: number, g: number, b: number): string {
	return r.toHex() + g.toHex() + b.toHex();
}

function getLinearColor(color: number): number {
	const normalizedColor = color / 255;

	if (normalizedColor <= 0.04045) {
		return normalizedColor / 12.92;
	} else {
		return ((normalizedColor + 0.055) / 1.055) ** 2.4;
	}
}

function getLuminance(r: number, g: number, b: number): number {
	return getLinearColor(r) * 0.2126 + getLinearColor(g) * 0.7152 + getLinearColor(b) * 0.0722;
}

function getContrastRatio(color1: number, color2: number = 0): number {
	return (color1 + 0.05) / (color2 + 0.05);
}

export function computeRatioAndColors(
	foregroundColor: string,
	backgroundColor: string,
): {
	contrastRatio: number;
	colors: number[][];
	inputColor: string;
} {
	const [fgR, fgG, fgB] = hex2rgb(foregroundColor);
	const [bgR, bgG, bgB] = hex2rgb(backgroundColor);
	const fgLRGB = getLuminance(fgR, fgG, fgB);
	const bgLRGB = getLuminance(bgR, bgG, bgB);

	const contrastRatio = fgLRGB > bgLRGB ? getContrastRatio(fgLRGB, bgLRGB) : getContrastRatio(bgLRGB, fgLRGB);

	return {
		contrastRatio: contrastRatio,
		colors: [
			[fgR, fgG, fgB],
			[bgR, bgG, bgB],
		],
		inputColor: 0.4 > bgLRGB ? '#fff' : '#000',
	};
}
